import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import {
  PDFDocument,
  StandardFonts,
  degrees,
  rgb
} from "pdf-lib";
import Swal from "sweetalert2";

// ==========================================
// DOCUMENT HELPERS
// ==========================================

export const getCashDocumentUrl = (value = "") => {
  const file = String(value).trim();

  if (!file) return "";

  if (/^https?:\/\//i.test(file)) {
    return file;
  }

  const isLocal = ["localhost", "127.0.0.1"].includes(
    window.location.hostname
  );

  const baseUrl = isLocal
    ? "http://localhost:3001"
    : "https://indigoinfra.in";

  const filename = file
    .split(/[?#]/)[0]
    .replace(/\\/g, "/")
    .split("/")
    .pop();

  if (!filename) return "";

  return `${baseUrl}/img/cash-entries/${encodeURIComponent(filename)}`;
};

export const getCashDocuments = (entry) => {
  const attachments = Array.isArray(entry?.image)
    ? entry.image
    : [];

  return attachments
    .map((attachment, index) => {
      const item =
        typeof attachment === "string"
          ? { url: attachment }
          : attachment;

      const source =
        item?.url ||
        item?.filename ||
        item?.path;

      if (!source) return null;

      const url = getCashDocumentUrl(source);

      if (!url) return null;

      const path = String(source).split(/[?#]/)[0];

      const extension = path
        .split(".")
        .pop()
        .toLowerCase();

      const mimeType = String(
        item.mimeType || item.mimetype || ""
      ).toLowerCase();

      const type =
        mimeType === "application/pdf" ||
          extension === "pdf"
          ? "pdf"
          : mimeType.startsWith("image/") ||
            [
              "jpg",
              "jpeg",
              "png",
              "gif",
              "webp"
            ].includes(extension)
            ? "image"
            : "other";

      return {
        url,
        name:
          item.originalName ||
          item.name ||
          item.label ||
          path.split("/").pop() ||
          `Document ${index + 1}`,
        type
      };
    })
    .filter(Boolean);
};

// ==========================================
// CONVERT IMAGE TO PDF
// ==========================================

const createImagePDF = async (file, rotation) => {
  const response = await fetch(file.url);

  if (!response.ok) {
    throw new Error(
      "Unable to load the attachment. Please try Open Original."
    );
  }

  const blob = await response.blob();
  const imageUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();

      element.onload = () => resolve(element);

      element.onerror = () => {
        reject(
          new Error("Unable to read the attached image.")
        );
      };

      element.src = imageUrl;
    });

    const angle = ((rotation % 360) + 360) % 360;
    const swapDimensions = angle === 90 || angle === 270;

    const canvas = document.createElement("canvas");

    canvas.width = swapDimensions
      ? image.naturalHeight
      : image.naturalWidth;

    canvas.height = swapDimensions
      ? image.naturalWidth
      : image.naturalHeight;

    const context = canvas.getContext("2d");

    if (!context || !canvas.width || !canvas.height) {
      throw new Error(
        "Unable to prepare the image for printing."
      );
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((angle * Math.PI) / 180);

    context.drawImage(
      image,
      -image.naturalWidth / 2,
      -image.naturalHeight / 2
    );

    const pdf = new jsPDF({
      orientation:
        canvas.width > canvas.height
          ? "landscape"
          : "portrait",
      unit: "mm",
      format: "a4",
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;

    const scale = Math.min(
      (pageWidth - margin * 2) / canvas.width,
      (pageHeight - margin * 2) / canvas.height
    );

    const width = canvas.width * scale;
    const height = canvas.height * scale;

    pdf.setProperties({
      title: file.name
    });

    pdf.addImage(
      canvas.toDataURL("image/jpeg", 0.95),
      "JPEG",
      (pageWidth - width) / 2,
      (pageHeight - height) / 2,
      width,
      height
    );

    return pdf.output("blob");
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

// ==========================================
// ADD FOOTER TO EVERY PRINTED PAGE
// ==========================================

const addPrintFooter = async (sourceBlob, entry) => {
  const source = await PDFDocument.load(
    await sourceBlob.arrayBuffer()
  );

  const output = await PDFDocument.create();

  const font = await output.embedFont(
    StandardFonts.Helvetica
  );

  const cleanText = (value) =>
    String(value || "")
      .replace(/[^\x20-\x7E]/g, "")
      .trim() || "NA";

  const voucherNumber = cleanText(
    entry?.generatedVoucherNumber ||
    entry?.voucherNumber
  );

  const requestNumber = cleanText(
    entry?.requestNumber
  );

  const texts = [
    "GENERATED BY STORE MANAGEMENT",
    `VOUCHER NO: ${voucherNumber}`,
    `REQUEST NO: ${requestNumber}`
  ];

  output.setTitle(
    `CASH DOCUMENT - ${requestNumber}`
  );

  for (const sourcePage of source.getPages()) {
    const crop = sourcePage.getCropBox();

    const embeddedPage = await output.embedPage(
      sourcePage,
      {
        left: crop.x,
        bottom: crop.y,
        right: crop.x + crop.width,
        top: crop.y + crop.height
      }
    );

    const rotation =
      ((sourcePage.getRotation().angle % 360) + 360) % 360;

    const swapDimensions =
      rotation === 90 || rotation === 270;

    const width = swapDimensions
      ? embeddedPage.height
      : embeddedPage.width;

    const height = swapDimensions
      ? embeddedPage.width
      : embeddedPage.height;

    // Keep the original visible page dimensions.
    // Scale content slightly to reserve footer space.
    const page = output.addPage([width, height]);

    const footerHeight = 32;

    const scale = Math.min(
      1,
      (height - footerHeight) / height
    );

    const contentWidth = width * scale;
    const contentHeight = height * scale;

    const left = (width - contentWidth) / 2;
    const bottom =
      footerHeight +
      (height - footerHeight - contentHeight) / 2;

    const placements = {
      0: {
        x: left,
        y: bottom
      },
      90: {
        x: left,
        y: bottom + contentHeight
      },
      180: {
        x: left + contentWidth,
        y: bottom + contentHeight
      },
      270: {
        x: left + contentWidth,
        y: bottom
      }
    };

    page.drawPage(embeddedPage, {
      ...(placements[rotation] || placements[0]),
      width: embeddedPage.width * scale,
      height: embeddedPage.height * scale,
      rotate: degrees(-rotation)
    });

    const margin = 18;
    const gap = 10;

    const columnWidth = Math.max(
      1,
      (width - margin * 2 - gap * 2) / 3
    );

    let fontSize = 7;

    for (const text of texts) {
      const textWidth = font.widthOfTextAtSize(text, 7);

      if (textWidth > columnWidth) {
        fontSize = Math.min(
          fontSize,
          (columnWidth / textWidth) * 7
        );
      }
    }

    page.drawLine({
      start: {
        x: margin,
        y: footerHeight - 5
      },
      end: {
        x: width - margin,
        y: footerHeight - 5
      },
      thickness: 0.5,
      color: rgb(0.75, 0.78, 0.82)
    });

    texts.forEach((text, index) => {
      const textWidth = font.widthOfTextAtSize(
        text,
        fontSize
      );

      const x =
        index === 0
          ? margin
          : index === 1
            ? (width - textWidth) / 2
            : width - margin - textWidth;

      page.drawText(text, {
        x,
        y: 11,
        size: fontSize,
        font,
        color: rgb(0.25, 0.28, 0.32)
      });
    });
  }

  return new Blob([await output.save()], {
    type: "application/pdf"
  });
};

// ==========================================
// DOCUMENT PREVIEW MODAL
// ==========================================

const CashDocumentPreviewModal = ({
  show,
  handleClose,
  entry,
  initialIndex = 0
}) => {
  const documents = getCashDocuments(entry);

  const [index, setIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [preparingPrint, setPreparingPrint] = useState(false);

  useEffect(() => {
    const selectedIndex = Number.isInteger(initialIndex)
      ? initialIndex
      : 0;

    setIndex(
      Math.min(
        Math.max(0, selectedIndex),
        Math.max(0, documents.length - 1)
      )
    );
  }, [entry, show, initialIndex, documents.length]);

  const activeDocument = documents[index];

  useEffect(() => {
    setRotation(0);
    setZoom(1);
    setLoading(true);
    setError(false);
  }, [activeDocument?.url, show]);

  // ========================================
  // PRINT / SAVE PDF
  // ========================================

  const handlePrintDocument = async () => {
    if (
      !activeDocument ||
      preparingPrint ||
      !["image", "pdf"].includes(activeDocument.type)
    ) {
      return;
    }

    const previewWindow = window.open("", "_blank");

    if (!previewWindow) {
      await Swal.fire({
        icon: "warning",
        title: "Popup Blocked",
        text: "Please allow popups to open the print preview."
      });

      return;
    }

    previewWindow.opener = null;
    previewWindow.document.title = "Preparing Print";

    Object.assign(previewWindow.document.body.style, {
      margin: "0",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "Arial, sans-serif",
      background: "#f5f7fa",
      color: "#495057"
    });

    previewWindow.document.body.textContent =
      "Preparing print preview...";

    setPreparingPrint(true);

    let pdfUrl = "";

    try {
      const file = activeDocument;

      const printEntry = {
        generatedVoucherNumber:
          entry?.generatedVoucherNumber ||
          entry?.voucherNumber ||
          "",
        requestNumber: entry?.requestNumber || ""
      };

      let sourceBlob;

      if (file.type === "image") {
        sourceBlob = await createImagePDF(
          file,
          rotation
        );
      } else {
        const response = await fetch(file.url);

        if (!response.ok) {
          throw new Error(
            "Unable to load the PDF attachment."
          );
        }

        sourceBlob = await response.blob();
      }

      const printableBlob = await addPrintFooter(
        sourceBlob,
        printEntry
      );

      if (previewWindow.closed) return;

      pdfUrl = URL.createObjectURL(printableBlob);
      previewWindow.location.replace(pdfUrl);

      const cleanupTimer = window.setInterval(() => {
        if (previewWindow.closed) {
          URL.revokeObjectURL(pdfUrl);
          window.clearInterval(cleanupTimer);
        }
      }, 2000);
    } catch (printError) {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }

      if (!previewWindow.closed) {
        previewWindow.close();
      }

      await Swal.fire({
        icon: "error",
        title: "Print Preview Failed",
        text:
          printError?.message ||
          "Unable to prepare the document for printing."
      });
    } finally {
      setPreparingPrint(false);
    }
  };

  const iconStyle = {
    border: "1px solid #dee2e6",
    borderRadius: "4px",
    padding: "5px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
    fontSize: "14px"
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title
          style={{
            fontSize: "20px",
            lineHeight: 1.5,
            overflowWrap: "anywhere"
          }}
        >
          Cash Document Preview — Request No:{" "}
          {entry?.requestNumber || "NA"}

          {(entry?.generatedVoucherNumber || entry?.voucherNumber) && (
            <span className="ms-3" style={{ font: "inherit", color: "inherit" }}>
              Voucher No:{" "}
              {entry.generatedVoucherNumber || entry.voucherNumber}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {activeDocument ? (
          <>
            {/* ATTACHMENT SELECTOR */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              {documents.map((file, fileIndex) => (
                <button
                  key={`${file.url}-${fileIndex}`}
                  type="button"
                  title={`View attachment ${fileIndex + 1}`}
                  aria-label={`View attachment ${fileIndex + 1}`}
                  aria-pressed={index === fileIndex}
                  disabled={preparingPrint}
                  onClick={() => setIndex(fileIndex)}
                  style={{
                    ...iconStyle,
                    background:
                      index === fileIndex
                        ? "#1565c0"
                        : "#ffffff",
                    color:
                      index === fileIndex
                        ? "#ffffff"
                        : "#1565c0",
                    opacity:
                      preparingPrint ? 0.65 : 1
                  }}
                >
                  <i
                    className={
                      file.type === "pdf"
                        ? "fa fa-file-pdf-o"
                        : "fa fa-paperclip"
                    }
                    aria-hidden="true"
                  />

                  <span>{fileIndex + 1}</span>
                </button>
              ))}
            </div>

            {/* IMAGE CONTROLS */}
            {activeDocument.type === "image" && (
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={preparingPrint}
                  onClick={() =>
                    setRotation(
                      (value) => value - 90
                    )
                  }
                >
                  ⟲ Rotate
                </Button>

                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={preparingPrint}
                  onClick={() =>
                    setRotation(
                      (value) => value + 90
                    )
                  }
                >
                  ⟳ Rotate
                </Button>

                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={preparingPrint}
                  onClick={() =>
                    setZoom((value) =>
                      Math.max(0.5, value - 0.2)
                    )
                  }
                >
                  − Zoom
                </Button>

                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={preparingPrint}
                  onClick={() =>
                    setZoom((value) =>
                      Math.min(3, value + 0.2)
                    )
                  }
                >
                  + Zoom
                </Button>

                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={preparingPrint}
                  onClick={() => {
                    setZoom(1);
                    setRotation(0);
                  }}
                >
                  Reset
                </Button>
              </div>
            )}

            <div
              className="small text-muted mb-2"
              style={{ overflowWrap: "anywhere" }}
            >
              {activeDocument.name}
            </div>

            {/* DOCUMENT PREVIEW */}
            <div
              style={{
                height: "65vh",
                overflow: "auto",
                background: "#f5f7fa",
                border: "1px solid #dee2e6",
                borderRadius: "6px",
                padding: "12px"
              }}
            >
              {activeDocument.type === "image" && (
                <>
                  {loading && !error && (
                    <div
                      className="text-center p-3"
                      role="status"
                    >
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        aria-hidden="true"
                      />
                      Loading document...
                    </div>
                  )}

                  {error ? (
                    <div className="text-center p-4 text-danger">
                      Unable to load image.
                      Please use Open Original.
                    </div>
                  ) : (
                    <div className="text-center">
                      <img
                        key={activeDocument.url}
                        src={activeDocument.url}
                        alt={activeDocument.name}
                        onLoad={() =>
                          setLoading(false)
                        }
                        onError={() => {
                          setLoading(false);
                          setError(true);
                        }}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          transform: `scale(${zoom}) rotate(${rotation}deg)`,
                          transformOrigin:
                            "center",
                          margin:
                            "24px auto"
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              {activeDocument.type === "pdf" && (
                <iframe
                  key={activeDocument.url}
                  src={activeDocument.url}
                  title={`Cash document ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: 0
                  }}
                />
              )}

              {activeDocument.type === "other" && (
                <div className="text-center p-4">
                  Preview unavailable for this file.
                  Please use Open Original.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center text-muted p-4">
            No cash documents attached.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {activeDocument && (
          <Button
            as="a"
            href={activeDocument.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="outline-secondary"
            size="sm"
          >
            Open Original
          </Button>
        )}

        {activeDocument &&
          ["image", "pdf"].includes(
            activeDocument.type
          ) && (
            <Button
              variant="primary"
              size="sm"
              disabled={preparingPrint}
              onClick={handlePrintDocument}
            >
              {preparingPrint ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  />
                  Preparing...
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="me-2"
                    aria-hidden="true"
                  >
                    <path d="M6 9V3h12v6" />
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                    <rect
                      x="6"
                      y="14"
                      width="12"
                      height="8"
                    />
                  </svg>
                  Print / Save PDF
                </>
              )}
            </Button>
          )}

        <Button
          variant="secondary"
          size="sm"
          onClick={handleClose}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CashDocumentPreviewModal;